import { Currency, CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
import CHARM_PERIPHERY_ABI from 'abis/CharmPeriphery.json'
import CHARM_VAULT_ABI from 'abis/CharmVault.json'
import { REFETCH_POLICY_FAST, WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { CHARM_PERIPHERY_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { ReactNode, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from '../../../hooks/useReadContractMultiple'
import { AppState } from '../../index'
import { selectPercent, setSlippageTolerance } from './actions'

export function useBurnCharmState(): AppState['burnCharm'] {
  return useAppSelector((state) => state.burnCharm)
}

export function useCharmVaultTokens(vaultAddress?: Address) {
  const { data } = useReadContractMultiple({
    contracts: [
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'token0',
      },
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'token1',
      },
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'decimals',
      },
    ],
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!vaultAddress,
    },
  })

  const token0Address = data?.[0]?.result as string
  const token1Address = data?.[1]?.result as string
  const vaultDecimals = data?.[2]?.result as number

  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  return { token0, token1, vaultDecimals }
}

const isValidJSBI = (value: JSBI | undefined | null): value is JSBI => {
  return value != null && JSBI.greaterThan(value, JSBI.BigInt(0))
}

export function useDerivedCharmBurnInfo(vaultAddress: Address | undefined): {
  sharesToBurn?: JSBI
  liquidityValue0?: CurrencyAmount<Currency>
  liquidityValue1?: CurrencyAmount<Currency>
  amount0Min?: CurrencyAmount<Currency>
  amount1Min?: CurrencyAmount<Currency>
  userVaultBalance?: CurrencyAmount<any>
  vaultTotalSupply?: JSBI
  error?: ReactNode
} {
  const { percent, slippageTolerance } = useBurnCharmState()
  const { account, chainId } = useConnection()

  const { token0, token1, vaultDecimals } = useCharmVaultTokens(vaultAddress)

  const peripheryAddress = CHARM_PERIPHERY_ADDRESSES[chainId]

  const { contracts, balanceIndex, totalSupplyIndex, vaultAmountsIndex } = useMemo(() => {
    const contracts: any[] = []
    let balanceIndex: number | undefined
    let totalSupplyIndex: number | undefined
    let vaultAmountsIndex: number | undefined

    if (vaultAddress && account) {
      balanceIndex = contracts.length
      contracts.push({
        address: vaultAddress,
        abi: CHARM_VAULT_ABI,
        functionName: 'balanceOf',
        args: [account],
      })
    }

    if (vaultAddress) {
      totalSupplyIndex = contracts.length
      contracts.push({
        address: vaultAddress,
        abi: CHARM_VAULT_ABI,
        functionName: 'totalSupply',
      })
    }

    if (peripheryAddress && vaultAddress) {
      vaultAmountsIndex = contracts.length
      contracts.push({
        address: peripheryAddress,
        abi: CHARM_PERIPHERY_ABI,
        functionName: 'getVaultAmounts',
        args: [vaultAddress],
      })
    }

    return { contracts, balanceIndex, totalSupplyIndex, vaultAmountsIndex }
  }, [vaultAddress, account, peripheryAddress])

  const { data: vaultResults } = useReadContractMultiple({
    contracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: contracts.length > 0,
    },
  })

  const userBalanceResult =
    balanceIndex !== undefined ? (vaultResults?.[balanceIndex]?.result as bigint | undefined) : undefined

  const userVaultBalance =
    userBalanceResult && vaultDecimals
      ? CurrencyAmount.fromRawAmount(
          { decimals: vaultDecimals, symbol: 'VAULT', name: 'Vault Token' } as any,
          userBalanceResult.toString()
        )
      : undefined

  const totalSupplyResult =
    totalSupplyIndex !== undefined ? (vaultResults?.[totalSupplyIndex]?.result as bigint | undefined) : undefined

  const vaultTotalSupply = totalSupplyResult ? JSBI.BigInt(totalSupplyResult.toString()) : undefined

  const vaultAmounts =
    vaultAmountsIndex !== undefined
      ? (vaultResults?.[vaultAmountsIndex]?.result as [bigint, bigint] | undefined)
      : undefined
  const vaultAmount0 = vaultAmounts?.[0] ? JSBI.BigInt(vaultAmounts[0].toString()) : undefined
  const vaultAmount1 = vaultAmounts?.[1] ? JSBI.BigInt(vaultAmounts[1].toString()) : undefined

  const selectedPercentage = useMemo(() => new Percent(percent, 100), [percent])

  const sharesToBurn = useMemo(() => {
    if (!userVaultBalance) return undefined
    return selectedPercentage.multiply(userVaultBalance.quotient).quotient
  }, [selectedPercentage, userVaultBalance])

  const liquidityValue0 = useMemo(() => {
    if (!vaultAmount0 || !sharesToBurn || !token0 || !isValidJSBI(vaultTotalSupply)) {
      return undefined
    }
    const amount = JSBI.divide(JSBI.multiply(vaultAmount0, sharesToBurn), vaultTotalSupply)
    return CurrencyAmount.fromRawAmount(token0, amount)
  }, [vaultAmount0, sharesToBurn, vaultTotalSupply, token0])

  const liquidityValue1 = useMemo(() => {
    if (!vaultAmount1 || !sharesToBurn || !vaultTotalSupply || !token1 || JSBI.equal(vaultTotalSupply, JSBI.BigInt(0)))
      return undefined
    const amount = JSBI.divide(JSBI.multiply(vaultAmount1, sharesToBurn), vaultTotalSupply)
    return CurrencyAmount.fromRawAmount(token1, amount)
  }, [vaultAmount1, sharesToBurn, vaultTotalSupply, token1])

  const amount0Min = useMemo(() => {
    if (!liquidityValue0) return undefined
    const slippageAdjustment = new Percent(Math.floor(slippageTolerance * 100), 10000)
    return liquidityValue0.multiply(new Percent(10000).subtract(slippageAdjustment)).divide(10000)
  }, [liquidityValue0, slippageTolerance])

  const amount1Min = useMemo(() => {
    if (!liquidityValue1) return undefined
    const slippageAdjustment = new Percent(Math.floor(slippageTolerance * 100), 10000)
    return liquidityValue1.multiply(new Percent(10000).subtract(slippageAdjustment)).divide(10000)
  }, [liquidityValue1, slippageTolerance])

  let error: ReactNode | undefined
  if (!account) {
    error = <>Connect Wallet</>
  }
  if (percent === 0) {
    error = error ?? <>Enter an amount</>
  }
  if (sharesToBurn && userVaultBalance && JSBI.greaterThan(sharesToBurn, userVaultBalance.quotient)) {
    error = <>Insufficient vault token balance</>
  }

  return {
    sharesToBurn,
    liquidityValue0,
    liquidityValue1,
    amount0Min,
    amount1Min,
    userVaultBalance,
    vaultTotalSupply,
    error,
  }
}

export function useBurnCharmActionHandlers(): {
  onPercentSelect: (percent: number) => void
  onSetSlippageTolerance: (slippageTolerance: number) => void
} {
  const dispatch = useAppDispatch()

  const onPercentSelect = useCallback(
    (percent: number) => {
      dispatch(selectPercent({ percent }))
    },
    [dispatch]
  )

  const onSetSlippageTolerance = useCallback(
    (slippageTolerance: number) => {
      dispatch(setSlippageTolerance({ slippageTolerance }))
    },
    [dispatch]
  )

  return {
    onPercentSelect,
    onSetSlippageTolerance,
  }
}
